import React, { useState, useEffect } from 'react';
import T from 'prop-types';
// components
import { FormattedMessage } from 'react-intl';
import Panel from 'components/Panel';
import Input from 'components/Input';
import Button, { TransparentBtn } from 'components/Button';
import Toggle from 'components/Toggle';
import TrashButton from 'components/TrashButton';
// utils
import classnames from 'classnames/bind';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { showToastrMessage } from 'utils';
// constants
import { MAX_WATER_METERS } from './constants';
// styles
import styles from './WaterMetersPanel.module.scss';

const cn = classnames.bind(styles);

let rowKeySeed = 0;
const nextKey = () => {
  rowKeySeed += 1;
  return `new-${rowKeySeed}`;
};

function buildRows(waterMeters = []) {
  return waterMeters.map((meter) => ({
    key: String(meter.id),
    id: meter.id,
    name: meter.name || '',
    active: meter.active !== false,
    _destroy: false,
  }));
}

const WaterMetersPanel = ({ farm, updateFarm }) => {
  const [rows, setRows] = useState(buildRows(farm.water_meters));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setRows(buildRows(farm.water_meters));
  }, [farm.id]);

  const visibleRows = rows.filter((row) => !row._destroy);

  const onNameChange = (key) => ({ target: { value } }) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, name: value } : row)));
  };

  const onActiveToggle = (key) => () => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, active: !row.active } : row)));
  };

  const onAddMeter = () => {
    if (visibleRows.length >= MAX_WATER_METERS) return;
    setRows((prev) => [...prev, { key: nextKey(), id: null, name: '', active: true, _destroy: false }]);
  };

  const onRemoveMeter = (key) => () => {
    setRows((prev) => prev
      .map((row) => (row.key === key ? { ...row, _destroy: true } : row))
      .filter((row) => !(row.key === key && !row.id)));
  };

  const hasEmptyName = visibleRows.some((row) => !row.name.trim());

  const onSave = () => {
    const attributes = rows
      .filter((row) => row.id || !row._destroy)
      .map((row, index) => ({
        id: row.id || undefined,
        name: row.name.trim(),
        position: index,
        active: row.active,
        _destroy: row._destroy || undefined,
      }));

    setIsSaving(true);
    updateFarm({ water_meters_attributes: attributes }, farm.id)
      .then((updatedFarm) => {
        setRows(buildRows(updatedFarm.water_meters));
        showToastrMessage('component.toastr.farm.infoUpdated');
      })
      .catch(toastResponseErrors)
      .finally(() => setIsSaving(false));
  };

  return (
    <Panel className="medium-12 xlarge-12 mb-15">
      <Panel.Heading title={<FormattedMessage id="general.waterMeters" />} />
      <Panel.Body className="ph-20">
        <p className={cn('hint')}>
          <FormattedMessage id="component.waterMeters.hint" />
        </p>

        {visibleRows.map((row) => (
          <div key={row.key} className={cn('meter-row')}>
            <div className={cn('meter-input')}>
              <Input
                value={row.name}
                onChange={onNameChange(row.key)}
                placeholder=""
                maxLength={120}
                hasNoMargin
              />
            </div>
            <Toggle isChecked={row.active} handleChange={onActiveToggle(row.key)} />
            <TrashButton onClick={onRemoveMeter(row.key)} />
          </div>
        ))}

        <div className={cn('actions')}>
          <TransparentBtn
            onClick={onAddMeter}
            disabled={visibleRows.length >= MAX_WATER_METERS}
          >
            + <FormattedMessage id="component.waterMeters.addMeter" />
          </TransparentBtn>
          {visibleRows.length >= MAX_WATER_METERS && (
            <span className={cn('limit-note')}>
              <FormattedMessage id="component.waterMeters.limitReached" values={{ max: MAX_WATER_METERS }} />
            </span>
          )}
        </div>

        <Button
          primary
          className="mv-20"
          disabled={isSaving || hasEmptyName}
          onClick={onSave}
        >
          <FormattedMessage id="general.button.save" />
        </Button>
      </Panel.Body>
    </Panel>
  );
};

WaterMetersPanel.propTypes = {
  farm: T.object.isRequired,
  updateFarm: T.func.isRequired,
};

export default WaterMetersPanel;
